import { useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { usePageContext } from 'vike-react/usePageContext';
import { getIndexName } from '../constants';
import {
  generateUrlFromIndexUiState,
  isCompanyProfilePath,
  parseUrlToIndexUiState,
  pathnameIsJobsListingPath,
  type IndexUiState,
} from '../utils/search-router/url-handling';
import { useSearchQuery, useSearchFilters, useSearchPage, useSearchSort } from '../contexts/SearchContext';
import { useNavigation } from '../contexts/NavigationContext';

/**
 * Hook to push a new indexUiState to the router and update local state, or just generate a URL for a given state.
 * Usage:
 *   const { pushIndexUiState, getUrlForIndexUiState } = usePushIndexUiState();
 *   const url = getUrlForIndexUiState(newUiState); // for render
 *   pushIndexUiState(newUiState); // for click handlers
 */
export function usePushIndexUiState() {
  const intl = useIntl();
  const memoIndexName = getIndexName(intl.locale);
  const { setQuery } = useSearchQuery();
  const { setFilters } = useSearchFilters();
  const { setPage } = useSearchPage();
  const { setSort } = useSearchSort();
  
  let ssrPathname = '';
  try {
    const urlOriginal = (usePageContext() as { urlOriginal?: string } | undefined)?.urlOriginal;
    if (typeof urlOriginal === 'string' && urlOriginal) {
      ssrPathname = new URL(urlOriginal, 'http://localhost').pathname;
    }
  } catch {
    // PageContext not available (tests, islands outside Vike): keep the old empty-path behaviour
  }

  // Try to get navigation context for immediate feedback
  let navigation: { startNavigation: () => void; completeNavigation: () => void } | null = null;
  try {
    navigation = useNavigation();
  } catch {
    // Navigation context not available, continue without it
  }

  function getUrlForIndexUiState(newUiState: IndexUiState, currentPath?: string): string {
    // On the server there is no window, so read the request path from pageContext. Building
    // server-side hrefs against an empty path dropped every pretty path segment the page has (a
    // role hub's card pills came out as `?normalisedTitle=a&normalisedTitle=b&...` query URLs
    // instead of the role x city page, TEC-697) and didn't match the href the browser builds.
    const path = currentPath ?? (typeof window !== 'undefined' ? window.location.pathname : ssrPathname);
    // A state that doesn't say which view it wants keeps the one on screen: the search panel (and
    // any other search-state change) builds its state without `view`, and a URL without it reads
    // as the cards list, so searching or filtering from the map used to throw the visitor out of
    // it. Pass `view` explicitly (e.g. 'list') to switch views.
    const withView: IndexUiState =
      'view' in newUiState || typeof window === 'undefined'
        ? newUiState
        : { ...newUiState, view: parseUrlToIndexUiState(window.location.pathname + window.location.search + window.location.hash).view };
    return generateUrlFromIndexUiState(withView, memoIndexName, path, {
      forceJobsListingForCompany: pathnameIsJobsListingPath(path),
    });
  }

  /**
   * `replace: true` updates the current history entry instead of adding one (live-as-you-type
   * search uses it after its first update, so Back doesn't step through every partial query).
   * A target equal to the current URL never adds an entry either.
   */
  function pushIndexUiState(newUiState: IndexUiState, options?: { replace?: boolean }): string {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const target =
      getUrlForIndexUiState(newUiState, currentPath) ||
      (typeof window !== 'undefined' ? window.location.pathname : '');
    const pathOnly = target.split('?')[0] || '';

    if (typeof window !== 'undefined' && isCompanyProfilePath(pathOnly)) {
      if (navigation) {
        navigation.startNavigation();
        setTimeout(() => {
          navigation?.completeNavigation();
        }, 400);
      }
      void navigate(target);
      return target;
    }

    // Show immediate feedback for search state changes
    if (navigation) {
      navigation.startNavigation();
      setTimeout(() => {
        navigation?.completeNavigation();
      }, 400);
    }

    // Optimistic UI updates - update state immediately
    if (typeof newUiState.query === 'string') {
      setQuery(newUiState.query);
    }
    if (typeof (newUiState as any).refinementList === 'object') {
      setFilters((newUiState as any).refinementList || {});
    }
    if (typeof newUiState.page === 'number') {
      const pageNum = Math.max(0, newUiState.page);
      setPage(pageNum);
    }
    if (typeof (newUiState as any).sort === 'string') {
      const s = (newUiState as any).sort as 'relevance' | 'date' | 'salary';
      if (s === 'relevance' || s === 'date' || s === 'salary') setSort(s);
    }

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (options?.replace || target === currentUrl) {
      window.history.replaceState({}, '', target);
    } else {
      window.history.pushState({}, '', target);
    }
    try {
      setTimeout(() => window.dispatchEvent(new Event('urlchange')), 0);
    } catch {}
    return target;
  }

  return { pushIndexUiState, getUrlForIndexUiState };
}
